import { useEffect, useRef, useState } from 'react'

/**
 * Names that were staged and no longer are — written, or dropped by the user.
 * Counted per occurrence, so two staged "Sand" rows of which one landed give
 * one "Sand".
 */
export const namesLeftStaging = (
  previous: readonly string[],
  next: readonly string[],
): string[] => {
  const remaining = [...next]
  return previous.filter((name) => {
    const at = remaining.indexOf(name)
    if (at === -1) return true
    remaining.splice(at, 1)
    return false
  })
}

/**
 * SCRUM-1032 QC round 7 follow-up — the names a quick-add chip should read as
 * already added.
 *
 * A chip read as added while its row was staged, then went back to "+" once
 * the row was written: the staged name was gone, and the list of what the
 * controller holds was never re-read, so "✓" only came back on a reload. A
 * name that leaves the staged list now stays marked while that list is
 * re-read; a row the user dropped goes back to "+" once the read lands.
 */
const useQuickAddNames = (
  existingNames: readonly string[],
  stagedNames: readonly string[],
  refetchExisting: () => unknown,
  /** The chips exist in the guided setup only; elsewhere nothing is read. */
  enabled = true,
): string[] => {
  const [awaiting, setAwaiting] = useState<string[]>([])
  const previousRef = useRef(stagedNames)
  const refetchRef = useRef(refetchExisting)
  refetchRef.current = refetchExisting

  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = stagedNames
    // Only a row leaving the staged list (written or dropped) can change what
    // the controller holds. Naming or renaming a staged row does not, and
    // each re-read is the whole list.
    if (!enabled || stagedNames.length >= previous.length) return
    const left = namesLeftStaging(previous, stagedNames).filter(Boolean)
    if (left.length === 0) return
    setAwaiting((current) => [...current, ...left])
    Promise.resolve()
      .then(() => refetchRef.current())
      .catch(() => undefined)
      .finally(() =>
        setAwaiting((current) => namesLeftStaging(current, left)),
      )
  }, [stagedNames, enabled])

  return [...existingNames, ...stagedNames, ...awaiting]
}

export default useQuickAddNames
